import { useState, useRef, useCallback } from 'react';
import { Send } from 'lucide-react';
import { Button } from '../Button';

interface ChatInputLabels {
  placeholder: string;
  send: string;
}

interface ChatInputProps {
  onSend: (message: string) => void;
  isLoading?: boolean;
  labels?: ChatInputLabels;
}

const DEFAULT_INPUT_LABELS: ChatInputLabels = {
  placeholder: 'Type a message...',
  send: 'Send',
};

export function ChatInput({ onSend, isLoading = false, labels: labelsProp }: ChatInputProps) {
  const labels = { ...DEFAULT_INPUT_LABELS, ...labelsProp };
  const [value, setValue] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleSend = useCallback(() => {
    const trimmed = value.trim();
    if (!trimmed || isLoading) return;
    onSend(trimmed);
    setValue('');
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  }, [value, isLoading, onSend]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  const handleInput = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setValue(e.target.value);
    const el = e.target;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  }, []);

  return (
    <div className="flex items-end gap-2 border-t border-gray-200 p-3 dark:border-gray-700">
      <textarea
        ref={textareaRef}
        value={value}
        onChange={handleInput}
        onKeyDown={handleKeyDown}
        placeholder={labels.placeholder}
        rows={1}
        className="flex-1 resize-none rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:placeholder-gray-500 dark:focus:border-blue-400"
        aria-label={labels.placeholder}
        data-testid="chat-input"
      />
      <Button
        variant="default"
        size="sm"
        onClick={handleSend}
        disabled={!value.trim() || isLoading}
        aria-label={labels.send}
        data-testid="chat-send-button"
      >
        <Send className="h-4 w-4" />
      </Button>
    </div>
  );
}
